import { type Epic, combineEpics } from 'redux-observable';
import {
  catchError,
  filter,
  from,
  fromEvent,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import i18n from 'i18next';
import { request, getApiErrorMessage } from '@/core';
import { closeSnackbar, openSnackbar } from '@/core/features/snackbar';
import {
  fetchCurrentUserRequest,
  loginRequest,
  mapCurrentUserToAuthUser,
} from '@/api/istaff/auth';
import { applyPreferredLanguage } from '@/i18n/languagePreference';
import {
  login,
  hydrateAuth,
  logout,
  setAuth,
  setUser,
  clearAuth,
  setLoginPending,
  setLoginError,
} from '../actions';

const loginPendingEpic: Epic = (action$) =>
  action$.pipe(
    filter(login.match),
    map(() => setLoginPending()),
  );

// new login attempt cancels a stale in-flight one — otherwise a slow failed request
// can resolve after a later successful one and surface a stray error toast.
const loginEpic: Epic = (action$) =>
  action$.pipe(
    filter(login.match),
    switchMap(({ payload }) =>
      from(loginRequest(payload)).pipe(
        map((response) => login.succeeded(response)),
        catchError((error) => of(login.failed(getApiErrorMessage(error)))),
      ),
    ),
  );

const loginSucceededEpic: Epic = (action$) =>
  action$.pipe(
    filter(login.succeeded.match),
    tap(({ payload }) => {
      localStorage.setItem('access_token', payload.tokens.access_token);
      localStorage.setItem('refresh_token', payload.tokens.refresh_token);
      applyPreferredLanguage(i18n, payload.user.preferred_language);
    }),
    mergeMap(({ payload }) => [
      setAuth({
        accessToken: payload.tokens.access_token,
        refreshToken: payload.tokens.refresh_token,
        user: mapCurrentUserToAuthUser(payload.user),
      }),
      closeSnackbar(),
    ]),
  );

const hydrateAuthEpic: Epic = (action$) =>
  action$.pipe(
    filter(hydrateAuth.match),
    request(() => fetchCurrentUserRequest(), hydrateAuth),
  );

const hydrateAuthSucceededEpic: Epic = (action$) =>
  action$.pipe(
    filter(hydrateAuth.succeeded.match),
    tap(({ payload }) =>
      applyPreferredLanguage(i18n, payload.preferred_language),
    ),
    map(({ payload }) => setUser(mapCurrentUserToAuthUser(payload))),
  );

const hydrateAuthFailedEpic: Epic = (action$) =>
  action$.pipe(
    filter(hydrateAuth.failed.match),
    mergeMap(({ payload }) => {
      if (payload.includes('401')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      return of(clearAuth());
    }),
  );

const sessionExpiredEpic: Epic = () =>
  fromEvent(window, 'auth:session-expired').pipe(map(() => clearAuth()));

const loginFailedEpic: Epic = (action$) =>
  action$.pipe(
    filter(login.failed.match),
    mergeMap(({ payload }) => [
      setLoginError(payload),
      openSnackbar({
        snackbarType: 'error',
        snackbarMessage: payload ?? i18n.t('login.login_error'),
      }),
    ]),
  );

const logoutEpic: Epic = (action$) =>
  action$.pipe(
    filter(logout.match),
    tap(() => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }),
    map(() => clearAuth()),
  );

export default combineEpics(
  loginPendingEpic,
  loginEpic,
  loginSucceededEpic,
  loginFailedEpic,
  hydrateAuthEpic,
  hydrateAuthSucceededEpic,
  hydrateAuthFailedEpic,
  logoutEpic,
  sessionExpiredEpic,
);
